"use client";

import React, { useState } from "react";
import { Copy, Check, Phone, Mail, MapPin, Loader, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
interface ContactCardProps {
  icon: React.ElementType;
  title: string;
  value: string;
  index: number;
}
function ContactCard({
  icon: Icon,
  title,
  value,
  index
}: ContactCardProps) {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    if (typeof navigator !== "undefined" && navigator.clipboard) {
      navigator.clipboard.writeText(value).then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      });
    }
  };
  return <Card data-index={index} className="card flex flex-row items-center p-5 gap-5 transition-all duration-300 bg-card/50 backdrop-blur-sm">
      <div className="bg-primary/5 p-3 rounded-lg text-primary shrink-0">
        <Icon className="size-6" strokeWidth={1.5} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-muted-foreground mb-1 tracking-tight">{title}</p>
        <p className="font-semibold text-primary truncate tracking-tight">{value}</p>
      </div>
      <TooltipProvider delayDuration={100}>
        <Tooltip open={copied ? true : undefined}>
          <TooltipTrigger asChild>
            <Button variant="ghost" size="icon" onClick={handleCopy} className="shrink-0 hover:bg-primary/5 hover:text-primary transition-colors" aria-label={`Kopiera ${title.toLowerCase()}`}>
              {copied ? <Check className="size-4 text-green-600" /> : <Copy className="size-4 text-muted-foreground" />}
            </Button>
          </TooltipTrigger>
          <TooltipContent side="top" className="bg-primary text-primary-foreground">
            <p className="text-xs font-medium">Kopierad!</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    </Card>;
}
function ContactFormInner() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div data-section-id="139295" className="card flex flex-col items-center justify-center p-12 text-center bg-muted/30 h-full min-h-[400px] animate-in fade-in zoom-in-95 duration-500">
        <div className="size-16 bg-primary/5 rounded-full flex items-center justify-center mb-6">
          <Check className="size-8 text-primary" />
        </div>
        <h3 className="text-[var(--font-size-h3)] leading-snug font-semibold text-primary mb-2 tracking-tight">Tack för ditt meddelande!</h3>
        <p className="text-[var(--font-size-body)] leading-[var(--line-height-body)] text-muted-foreground mb-8 max-w-md text-balance">
          {successMessage}
        </p>
        <Button onClick={resetForm} variant="outline" className="bg-background border-primary text-primary hover:bg-primary/5 rounded-lg">
          Skicka ett nytt meddelande
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
        <FormField control={control} name="name" rules={{
        required: "Ditt namn är obligatoriskt",
        minLength: {
          value: 2,
          message: "Namnet måste vara minst 2 tecken"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-primary font-medium tracking-tight">Ditt namn</FormLabel>
              <FormControl>
                <Input placeholder="Anna Svensson" {...field} className="bg-background border-input rounded-lg focus-visible:ring-primary" />
              </FormControl>
              <FormDescription className="text-xs">Ange ditt fullständiga namn.</FormDescription>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "E-postadress är obligatorisk",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Ogiltig e-postadress"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-primary font-medium tracking-tight">E-postadress</FormLabel>
              <FormControl>
                <Input type="email" placeholder="anna@logikekonomi.se" {...field} className="bg-background border-input rounded-lg focus-visible:ring-primary" />
              </FormControl>
              <FormDescription className="text-xs">Vi kontaktar dig via e-post.</FormDescription>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="phone" rules={{
      required: "Telefonnummer är obligatoriskt",
      pattern: {
        value: /^[0-9+\-\s()]*$/,
        message: "Ogiltigt telefonnummer"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-primary font-medium tracking-tight">Telefonnummer</FormLabel>
            <FormControl>
              <Input type="tel" placeholder="070-000 00 00" {...field} className="bg-background border-input rounded-lg focus-visible:ring-primary" />
            </FormControl>
            <FormDescription className="text-xs">Ange ett telefonnummer vi kan nå dig på.</FormDescription>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="message" rules={{
      required: "Meddelande är obligatoriskt",
      minLength: {
        value: 10,
        message: "Meddelandet måste vara minst 10 tecken"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-primary font-medium tracking-tight">Meddelande</FormLabel>
            <FormControl>
              <Textarea placeholder="Berätta kort om dina behov inom redovisning, lön eller rådgivning..." className="min-h-[120px] resize-y bg-background border-input rounded-lg focus-visible:ring-primary" {...field} />
            </FormControl>
            <FormDescription className="text-xs">Beskriv gärna ditt ärende för snabbare hantering.</FormDescription>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="preferred_contact" render={({
      field
    }) => <FormItem className="space-y-3">
            <FormLabel className="text-primary font-medium tracking-tight">Önskad kontaktmetod</FormLabel>
            <FormControl>
              <div className="flex flex-wrap gap-4">
                {["E-post", "Telefon"].map((option, i) => <label key={option} data-index={i} className={`flex items-center gap-3 px-4 py-3 rounded-lg border cursor-pointer transition-all duration-200 ${field.value === option ? "border-primary bg-primary/5 ring-1 ring-primary/10" : "border-input bg-background hover:bg-muted/50"}`}>
                    <div className={`size-4 rounded-full border flex items-center justify-center ${field.value === option ? "border-primary" : "border-muted-foreground"}`}>
                      {field.value === option && <div className="size-2 rounded-full bg-primary" />}
                    </div>
                    <input type="radio" className="sr-only" {...field} value={option} checked={field.value === option} />
                    <span className="text-sm font-medium tracking-tight">{option}</span>
                  </label>)}
              </div>
            </FormControl>
            <FormDescription className="text-xs">Välj hur du föredrar att bli kontaktad.</FormDescription>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-4 bg-destructive/5 border border-destructive/20 rounded-lg text-destructive text-sm flex items-start gap-3">
          <div className="mt-0.5"></div>
          <p className="tracking-tight">{submitError}</p>
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full sm:w-auto min-w-[200px] h-12 text-base group bg-accent hover:bg-accent/90 text-accent-foreground rounded-lg transition-all duration-300">
        {isSubmitting ? <>
            <Loader className="animate-spin mr-2 size-5" />
            Skickar...
          </> : <>
            Skicka förfrågan
            <ArrowRight className="ml-2 size-4 transition-transform group-hover:translate-x-1" />
          </>}
      </Button>
    </Form>;
}
export default function TalkToASpecialistToday() {
  const {
    ref: sectionRef,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const contactDetails = [{
    icon: Phone,
    title: "Ring oss",
    value: "+46 73 655 09 11"
  }, {
    icon: Mail,
    title: "Mejla oss",
    value: "info@logikekonimi.se"
  }, {
    icon: MapPin,
    title: "Besök oss",
    value: "Ranhammarsvägen 20, 168 67 Bromma"
  }];
  return <section id="skicka-en-förfrågan" className="bg-background section relative overflow-hidden ![padding:120px_48px_60px_48px]" ref={sectionRef}>
      {}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute -top-[20%] -right-[10%] w-[50%] h-[50%] rounded-full bg-primary/3 blur-[120px]" />
        <div className="absolute -bottom-[20%] -left-[10%] w-[50%] h-[50%] rounded-full bg-accent/3 blur-[120px]" />
      </div>

      <div className="container mx-auto relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-start">
          
          {}
          <div className={`lg:col-span-5 space-y-10 transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <div className="space-y-4">
              
              <h2 className="text-[var(--font-size-h2)] leading-tight font-bold tracking-tight text-primary text-balance">
                Prata med en rådgivare idag
              </h2>
              <p className="text-[var(--font-size-body)] leading-[var(--line-height-body)] text-muted-foreground max-w-md text-balance tracking-tight">Kontakta oss för personlig rådgivning eller frågor om våra tjänster inom redovisning. Vi återkopplar personligen inom 24 timmar.</p>
            </div>

            <div className="space-y-4">
              {contactDetails.map((detail, index) => <ContactCard key={index} index={index} icon={detail.icon} title={detail.title} value={detail.value} />)}
            </div>
            
            <div className="pt-6 border-t border-[var(--card-border)]">
              <p className="text-sm text-muted-foreground tracking-tight">
                <strong className="font-semibold text-primary">Öppettider:</strong> Måndag–Fredag: 08:30–17:00
              </p>
            </div>
          </div>

          {}
          <div className={`lg:col-span-7 transition-all duration-1000 delay-200 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <div className="card bg-card p-6 sm:p-10 relative overflow-hidden">
              {}
              <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-primary via-accent to-primary opacity-30" />
              
              <div className="mb-8">
                <h3 className="text-[var(--font-size-h3)] leading-snug font-semibold text-primary mb-2 tracking-tight">Skicka ett meddelande</h3>
                <p className="text-[var(--font-size-body)] leading-[var(--line-height-body)] text-muted-foreground tracking-tight">Fyll i formuläret nedan så kontaktar vi dig så snart som möjligt.</p>
              </div>

              <FormProvider formKey="inquiry_form_contact" formId="inquiry_form_contact" sectionName="TalkToASpecialistToday" defaultValues={{
              name: "",
              email: "",
              phone: "",
              message: "",
              preferred_contact: "E-post"
            }} submitText="Skicka förfrågan" successMessage="Tack för ditt meddelande! En av våra rådgivare kommer att kontakta dig inom kort för att diskutera dina behov." errorMessage="Ett fel uppstod när meddelandet skulle skickas. Vänligen försök igen eller kontakta oss direkt via telefon.">
                <ContactFormInner />
              </FormProvider>
            </div>
          </div>

        </div>
      </div>
    </section>;
}